import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { Eyebrow, Skeleton, Tabs, defaultAvatarFor, formatCount, SoftAgent } from '@algoboo/ui'
import { agentManageQuery, useAgentManage } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { SignInPrompt, useSession } from '../components/session'
import { NoSuchView, QueryError } from '../components/widgets'
import { viewOf } from '../lib/views'
import { ModelPolicyForm } from '../components/strategy/ModelPolicyForm'
import { LaunchChecklist } from '../components/owner/LaunchChecklist'
import { StatusControls, StrategySection } from '../components/manage/ManageControls'
import { AvatarSection, Settings, Versions } from '../components/manage/ProfileSections'
import { Inbox, Keys, Usage } from '../components/manage/OpsSections'
import { TokenLaunch, TokenPolicy } from '../components/manage/TokenSections'
import { OperationsPanel } from '../components/manage/OperationsPanel'
import { prefetchSignedIn } from '../lib/route-data'

const SECTIONS = ['settings', 'avatar', 'strategy', 'model', 'versions', 'usage', 'inbox', 'keys', 'token'] as const
type Section = (typeof SECTIONS)[number]

export const Route = createFileRoute('/a/$handle/manage')({
  validateSearch: z.object({ section: z.string().max(64).optional().catch(undefined) }),
  // the console only: the launch checklist opens or stays one line by what it knows when it first renders, and it
  // reads the agent's token itself (prefetching it here would fold a finished checklist the page shows open)
  loader: ({ context: { queryClient: qc }, params: { handle } }) => prefetchSignedIn(qc, () => [qc.ensureQueryData(agentManageQuery(handle))]),
  component: ManagePage,
})

function ManagePage() {
  const { handle } = Route.useParams()
  const named = viewOf<Section>(Route.useSearch().section, SECTIONS, 'settings')
  const section: Section = named.view ?? 'settings'
  const navigate = useNavigate({ from: '/a/$handle/manage' })
  const { me, loading } = useSession()
  const q = useAgentManage(handle, !!me)
  usePageMeta(`Manage @${handle}`)

  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title={`Manage @${handle}`} body="Only the agent's owner can open this page. Sign in with the wallet that created it." />
  if (q.isLoading) return <Skeleton className="h-64 rounded-xl" />
  if (q.error) return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  const m = q.data!
  const a = m.agent
  if (named.unknown !== null) return <NoSuchView name={named.unknown} page={`@${a.handle}'s owner console`} />

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <SoftAgent avatar={a.avatar ?? defaultAvatarFor(a.handle)} size={84} framing="full" title="" />
        <div className="min-w-0 flex-1">
          <Eyebrow>Owner console</Eyebrow>
          <h1 className="rh-h1 font-display font-semibold">{a.name}</h1>
          <p className="font-mono text-[12.5px] text-ink-3">
            <Link to="/a/$handle" params={{ handle }} className="hover:text-ink hover:underline">
              @{a.handle}
            </Link>{' '}
            · version {a.currentVersion?.version ?? '—'} · {formatCount(m.permissionsCount)} permissions
          </p>
        </div>
        <StatusControls m={m} />
      </div>
      <LaunchChecklist m={m} />
      <Tabs
        idBase="manage"
        label="Manage sections"
        value={section}
        onChange={(s) => void navigate({ search: { section: s === 'settings' ? undefined : s }, replace: true })}
        tabs={[
          { value: 'settings', label: 'Settings' },
          { value: 'avatar', label: 'Avatar' },
          { value: 'strategy', label: 'Strategy' },
          { value: 'model', label: 'Model' },
          { value: 'versions', label: 'Versions', count: m.versions.length },
          { value: 'usage', label: 'Usage & credits' },
          { value: 'inbox', label: 'Inbox', count: m.inbox.filter((i) => i.status === 'pending').length },
          { value: 'keys', label: 'API keys' },
          { value: 'token', label: 'Token' },
        ]}
      />
      <section id="manage-panel" role="tabpanel" aria-labelledby={`manage-tab-${section}`}>
        {section === 'settings' && <Settings m={m} />}
        {section === 'avatar' && <AvatarSection m={m} />}
        {section === 'strategy' && <StrategySection m={m} />}
        {section === 'model' && <ModelPolicyForm m={m} />}
        {section === 'versions' && <Versions m={m} />}
        {section === 'usage' && <Usage m={m} />}
        {section === 'inbox' && <Inbox m={m} />}
        {section === 'keys' && <Keys m={m} />}
        {section === 'token' && (
          <>
            <TokenLaunch m={m} />
            <OperationsPanel m={m} />
            <TokenPolicy m={m} />
          </>
        )}
      </section>
    </div>
  )
}
